import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useId, useRef, useImperativeHandle, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@skatteetaten/ds-buttons';
import { dsI18n, getCommonClassNameDefault, SkatteetatenLogo, Separator } from '@skatteetaten/ds-core-utils';
import { CancelSVGpath } from '@skatteetaten/ds-icons';
import { Heading } from '@skatteetaten/ds-typography';
import './styles.css';


const getModalPaddingDefault = ()=>'l';
const getModalVariantDefault = ()=>'outline';
const getModalDismissOnOutsideClickDefault = ()=>true;
const getModalDismissOnEscDefault = ()=>true;

var styles = {
    "modal": "Modal-module_modal__Kyr-Y",
    "modalContainer": "Modal-module_modalContainer__muf75",
    "modalLogo": "Modal-module_modalLogo__Vaf6E",
    "modalHeading": "Modal-module_modalHeading__uwT1R",
    "modalHeadingNoPadding": "Modal-module_modalHeadingNoPadding__t2hkH",
    "modalIllustration": "Modal-module_modalIllustration__jbA32",
    "modalNoBorder": "Modal-module_modalNoBorder__ReNVP",
    "modalPaddingNONE": "Modal-module_modalPaddingNONE__02HaX",
    "modalPaddingS": "Modal-module_modalPaddingS__O93ax",
    "modalPaddingM": "Modal-module_modalPaddingM__c5Wi2",
    "modalPaddingL": "Modal-module_modalPaddingL__IbVyg",
    "modalPaddingMEGA": "Modal-module_modalPaddingMEGA__wWHwS",
    "modalNoPaddingTop": "Modal-module_modalNoPaddingTop__REBWr",
    "closeButton": "Modal-module_closeButton__jgbDq",
    "srOnly": "Modal-module_srOnly__W8dQv"
};

const Modal = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), classNames, lang, 'data-testid': dataTestId, dismissOnEsc = getModalDismissOnEscDefault(), dismissOnOutsideClick = getModalDismissOnOutsideClickDefault(), hideCloseButton, hideTitle, imageSource, imageSourceAltText, padding = getModalPaddingDefault(), title, variant = getModalVariantDefault(), shadowRootNode, onClose, renderIcon, children }, ref)=>{
    const headingId = `modalHeadingId-${useId()}`;
    const { t } = useTranslation('ds_overlays', {
        i18n: dsI18n
    });
    const statusFlagRef = useRef({
        mouseDownCaptured: false
    });
    const modalRef = useRef(null);
    useImperativeHandle(ref, ()=>modalRef?.current);
    const [isAutoOpened, setIsAutoOpened] = useState();
    useEffect(()=>{
        const dialog = modalRef.current;
        if (dialog) {
            const originalMethod = dialog.showModal;
            dialog.showModal = function showModal(...args) {
                setIsAutoOpened(document.activeElement?.nodeName === 'BODY');
                originalMethod.apply(dialog, args);
            };
        }
    }, []);
    useEffect(()=>{
        const dialog = modalRef.current;
        const handleClose = ()=>{
            if (isAutoOpened) {
                setIsAutoOpened(false);
                //TODO skiplink kan ligge i shadowDom. Da kan vi ikke finne den med querySelector
                // Denne modalen kan også eksistere i en shadowDom. ;-)
                const skipLink = document.querySelector('a[data-skip-link]');
                if (skipLink) {
                    skipLink?.focus();
                } else {
                    const prevTabIndex = document.body.tabIndex;
                    document.body.tabIndex = -1;
                    document.body.focus();
                    document.body.tabIndex = prevTabIndex;
                }
            }
        };
        if (dialog) {
            dialog.addEventListener('close', handleClose);
        }
        return ()=>{
            if (dialog) {
                dialog.removeEventListener('close', handleClose);
            }
        };
    }, [
        isAutoOpened
    ]);
    useEffect(()=>{
        /**
       * Hvis konsument endrer children i en åpen modal, vil fokus settes til body og dismissOnEsc vil slutte å fungere.
       * Setter derfor fokus tilbake til modalen.
       */ if (!dismissOnEsc && modalRef.current?.open && document.activeElement?.nodeName === 'BODY') {
            modalRef.current.focus();
        }
    }, [
        children,
        dismissOnEsc
    ]);
    const isClickOutside = (event)=>{
        if (!(event.currentTarget instanceof HTMLElement)) {
            return true;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        return rect.left > event.clientX || rect.right < event.clientX || rect.top > event.clientY || rect.bottom < event.clientY;
    };
    const handleMouseEvent = (event)=>{
        if (!dismissOnOutsideClick || !(event.target instanceof HTMLElement)) {
            return;
        }
        if (event.type === 'mousedown') {
            statusFlagRef.current.mouseDownCaptured = false;
        }
        if (isClickOutside(event)) {
            if (event.type === 'mouseup' && statusFlagRef.current.mouseDownCaptured) {
                onClose?.();
                modalRef.current?.close();
            } else {
                statusFlagRef.current.mouseDownCaptured = true;
            }
        }
    };
    const hideTitleClassName = hideTitle ? styles.srOnly : '';
    const hideOutlineClassName = variant === 'plain' || variant === 'important' ? styles.modalNoBorder : '';
    const paddingClassName = styles[`modalPadding${padding.toUpperCase()}`];
    const noPaddingTop = imageSource ? styles.modalNoPaddingTop : '';
    const headingNoPaddingClassName = padding === 'mega' ? styles.modalHeadingNoPadding : '';
    if (variant === 'important') {
        console.warn('Modal: Varianten "important" er deprekert og vil bli fjernet i neste major versjon.');
    }
    return /*#__PURE__*/ jsx("dialog", {
        ref: modalRef,
        id: id,
        className: `${styles.modal} ${hideOutlineClassName} ${className} ${classNames?.container ?? ''}`.trim(),
        lang: lang,
        "data-testid": dataTestId,
        "aria-labelledby": headingId,
        autoFocus: true,
        /* Må bruke onCancel i kombinasjon med onKeyDown siden to trykk på esc lukker modalen i chrome (https://issues.chromium.org/issues/41491338) */ onCancel: (e)=>!dismissOnEsc && e.preventDefault(),
        onKeyDown: (e)=>{
            if (e.key === 'Escape') {
                if (dismissOnEsc) {
                    onClose?.();
                    modalRef.current?.close();
                } else {
                    e.preventDefault();
                }
            }
        },
        onMouseUp: handleMouseEvent,
        onMouseDown: handleMouseEvent,
        children: /*#__PURE__*/ jsxs("div", {
            tabIndex: -1,
            className: styles.modalContainer,
            children: [
                !hideCloseButton && /*#__PURE__*/ jsx(IconButton, {
                    className: styles.closeButton,
                    svgPath: CancelSVGpath,
                    title: t('modal.CloseModal'),
                    onClick: ()=>{
                        onClose?.();
                        modalRef.current?.close();
                    }
                }),
                imageSource && /*#__PURE__*/ jsx("img", {
                    src: imageSource,
                    alt: imageSourceAltText ?? '',
                    className: `${styles.modalIllustration} ${classNames?.image ?? ''}`.trim()
                }),
                /*#__PURE__*/ jsxs("div", {
                    className: `${paddingClassName} ${noPaddingTop}`.trim(),
                    children: [
                        variant === 'important' && /*#__PURE__*/ jsx(SkatteetatenLogo, {
                            className: styles.modalLogo
                        }),
                        renderIcon && /*#__PURE__*/ jsx("div", {
                            children: renderIcon?.()
                        }),
                        /*#__PURE__*/ jsx(Heading, {
                            className: `${styles.modalHeading} ${headingNoPaddingClassName} ${hideTitleClassName}`.trim(),
                            id: headingId,
                            as: 'h1',
                            level: 3,
                            hasSpacing: true,
                            children: title
                        }),
                        children
                    ]
                }),
                variant === 'important' && /*#__PURE__*/ jsx(Separator, {})
            ]
        })
    });
});
Modal.displayName = 'Modal';

export { Modal, getModalDismissOnEscDefault, getModalDismissOnOutsideClickDefault, getModalPaddingDefault, getModalVariantDefault };
